import React, { useEffect, useRef, useState } from 'react';
import { sicrediService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import { agendaVencida, leituraAtrasada, mensagemDaSincronizacao, textoDaProxima, textoDasLeiturasDeHoje } from '../utils/extratoSicrediApi';
import { TONE_TEXT } from '../utils/statusColors';

/**
 * A linha do extrato pela API no card da conta Sicredi (30/10/2026): quando foi a última leitura, quando será a próxima, e
 * o "Atualizar agora" para admin. Em componente próprio porque tem relógio: o "hoje/amanhã" e o atraso andam com a hora,
 * e o relógio no BankAccountsTab redesenharia a tela inteira por isso.
 *
 * ⭐ O horário vem do SERVIDOR (backend #585, 31/10/2026): `proximaLeituraEm`, `ultimoDisparoEsperadoEm` e `horasDeHoje`
 * da própria conta. Quando o disparo que a agenda chamava de próximo passa, a tela relê as contas uma vez, para a leitura
 * nova e a agenda nova virem do servidor, e não de uma conta feita aqui.
 *
 * Fica no lugar do "Último extrato" nas contas pela API: a sincronização grava o mesmo log de importação, então as duas
 * linhas diriam a mesma hora duas vezes.
 */
export default function ExtratoSicrediDoCartao({ acc, podeAtualizar, aoAtualizar, avisar }: {
  acc: any;
  podeAtualizar: boolean;
  aoAtualizar: () => Promise<unknown> | void;
  avisar: (mensagem: string, tipo?: 'success' | 'error') => void;
}) {
  const [agora, setAgora] = useState(() => new Date());
  const [atualizando, setAtualizando] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const temAgenda = !!acc.proximaLeituraEm || !!acc.ultimoDisparoEsperadoEm;
  const atrasada = leituraAtrasada(acc.balanceCheckedAt, acc, agora);
  const leiturasDeHoje = textoDasLeiturasDeHoje(acc);

  // A agenda é do instante em que a conta foi lida. Passado o disparo que ela chamava de próximo, relê UMA vez por disparo.
  const relidoPara = useRef<string | null>(null);
  useEffect(() => {
    if (!agendaVencida(acc, agora) || relidoPara.current === acc.proximaLeituraEm) return;
    relidoPara.current = acc.proximaLeituraEm;
    void aoAtualizar();
  }, [agora, acc.proximaLeituraEm]); // eslint-disable-line react-hooks/exhaustive-deps

  const atualizarAgora = async (e: React.MouseEvent) => {
    // O cartão inteiro abre a conta no clique.
    e.stopPropagation();
    if (atualizando) return;
    setAtualizando(true);
    try {
      const r = await sicrediService.sincronizarExtrato();
      avisar(mensagemDaSincronizacao(r));
      await aoAtualizar();
    } catch (err: any) {
      avisar(err?.response?.data?.message || err?.message || 'Não consegui atualizar o extrato do Sicredi', 'error');
    } finally {
      setAtualizando(false);
      setAgora(new Date());
    }
  };

  return (
    <div className="mt-1 text-xs" data-extrato-api={acc.id}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-central-elevado text-central-texto-fraco border border-central-borda" data-selo-fonte>
          API Sicredi
        </span>
        <span className={atrasada ? TONE_TEXT.warning : 'text-slate-500'} data-ultima-leitura>
          {acc.balanceCheckedAt ? `Atualizado ${fmtDateTime(acc.balanceCheckedAt)}` : 'Ainda sem leitura pela API'}
          {atrasada && acc.balanceCheckedAt && ' · atrasado'}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        {temAgenda
          ? <span className="text-slate-500" data-proxima-leitura title={leiturasDeHoje || undefined}>{textoDaProxima(acc, agora)}</span>
          : <span />}
        {podeAtualizar && (
          <button
            type="button"
            onClick={atualizarAgora}
            disabled={atualizando}
            className="px-2 py-0.5 rounded-md border border-central-borda text-central-texto-fraco hover:text-central-texto hover:bg-central-elevado disabled:opacity-50"
            data-atualizar-agora
          >
            {atualizando ? 'Atualizando…' : 'Atualizar agora'}
          </button>
        )}
      </div>
      {temAgenda && leiturasDeHoje && <p className="text-[11px] text-slate-500 mt-0.5" data-leituras-de-hoje>{leiturasDeHoje}</p>}
    </div>
  );
}
